import type { CustomDomainConfig, LandingConfig } from '@dosfilos/domain';
import type { UpdateTenantSettingsParams } from '../../lib/api/tenant.api';
import { normalizeFeaturedEvents } from '../../lib/featured-events';
import { normalizeContact, normalizePillars } from '../../lib/landing-content';
import { normalizeFaculty } from '../../lib/landing-faculty';
import type { Rubric as TenantRubricUi } from '../sis/components/RubricEditor';

// ─── Configuración del seminario: 6 pestañas ────────────────────────────────
// Eran 8, en mayúsculas, y la última (Módulos) quedaba cortada. Se agrupan por
// lo que el seminario busca, no por cómo se fue construyendo la pantalla:
//
//   Identidad          — nombre, slug, color, logos y localización
//   Página pública     — landing y dominio
//   Académico          — escala de notas, vigencia de derechos de catálogo y
//                        rúbricas (criterios de evaluación)
//   Marco institucional — misión, versículo, declaración doctrinal y TMAI
//   Notificaciones
//   Módulos
//
// SOLO CAMBIAN DE LUGAR. Lo que se guarda con el botón «Guardar» lo arma
// `buildTenantSettingsPayload`, igual que antes de la reorganización; la escala
// de notas, las notificaciones y los módulos se guardan con su propio botón.

export const SETTINGS_TABS = [
  'identity',
  'public',
  'academic',
  'framework',
  'notifications',
  'modules',
] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

/** Las pestañas viejas (8) apuntan a la nueva que contiene lo que mostraban,
 *  para que un enlace guardado o una ayuda vieja sigan funcionando. */
const LEGACY_TABS: Record<string, SettingsTab> = {
  logo: 'identity',
  landing: 'public',
  domain: 'public',
  rubrics: 'academic',
};

/** La pestaña de la URL (`?tab=…`). Una vieja se traduce; una desconocida, o
 *  ninguna, cae en la primera. */
export function settingsTabFromParam(param: string | null | undefined): SettingsTab {
  if (!param) return 'identity';
  if ((SETTINGS_TABS as readonly string[]).includes(param)) return param as SettingsTab;
  return LEGACY_TABS[param] ?? 'identity';
}

/** En qué pestaña está cada dato que guarda el botón «Guardar» (y los que se
 *  guardan solos). Un dato, una pestaña. */
export const SETTINGS_FIELD_TAB = {
  name: 'identity',
  slug: 'identity',
  primaryColor: 'identity',
  logoUrl: 'identity',
  documentLogoUrl: 'identity',
  timezone: 'identity',
  dateFormat: 'identity',
  timeFormat: 'identity',
  landing: 'public',
  customDomains: 'public',
  gradingScale: 'academic',
  studentCode: 'academic',
  catalogRightsExpiryYears: 'academic',
  mission: 'framework',
  tmaiSiteAbbreviation: 'framework',
  doctrinalStatement: 'framework',
  rubrics: 'academic',
  notifications: 'notifications',
  modules: 'modules',
} as const satisfies Record<string, SettingsTab>;

export interface TenantSettingsDraft {
  tenantId: string;
  name: string;
  slug: string;
  primaryColor: string;
  logoUrl: string | undefined;
  documentLogoUrl: string | undefined;
  missionText: string;
  missionScripture: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  tmaiSiteAbbreviation: string;
  catalogRightsExpiryYears: number | '';
  doctrinalStatement: Record<string, unknown> | null;
  landing: LandingConfig;
  customDomains: CustomDomainConfig;
  rubrics: TenantRubricUi[];
}

/** ¿Cambió la vigencia de los derechos de catálogo? Devuelve el valor a guardar
 *  (`null` = el seminario no aplica la regla) o `undefined` si no cambió.
 *
 *  SE GUARDA POR EL CALLABLE `updateTenantConfig`, no por el lote del botón: las
 *  reglas de Firestore no dejan al `tenantAdmin` escribir esa clave desde el
 *  navegador, y el lote la recibía y no la escribía. La pantalla decía
 *  «guardado» y el valor se perdía (verificado en el emulador, 2026-10-16). */
export function catalogRightsChange(
  draft: number | '',
  saved: number | null,
): number | null | undefined {
  const next = draft === '' || Number(draft) === 0 ? null : Number(draft);
  return next === (saved ?? null) ? undefined : next;
}

/** Lo que guarda el botón «Guardar» de Configuración. Es el mismo armado que
 *  tenía la pantalla antes de reorganizar las pestañas (test de no regresión). */
export function buildTenantSettingsPayload(d: TenantSettingsDraft): UpdateTenantSettingsParams {
  return {
    tenantId: d.tenantId,
    name: d.name,
    slug: d.slug,
    primaryColor: d.primaryColor,
    ...(d.logoUrl !== undefined ? { logoUrl: d.logoUrl } : {}),
    documentLogoUrl: d.documentLogoUrl ?? null,
    mission: d.missionText.trim()
      ? {
          text: d.missionText.trim(),
          ...(d.missionScripture.trim() ? { scripture: d.missionScripture.trim() } : {}),
        }
      : null,
    ...(d.timezone ? { timezone: d.timezone } : {}),
    ...(d.dateFormat
      ? { dateFormat: d.dateFormat as 'dd-mm-yyyy' | 'mm-dd-yyyy' | 'yyyy-mm-dd' }
      : {}),
    ...(d.timeFormat ? { timeFormat: d.timeFormat as '24h' | '12h' } : {}),
    tmaiSiteAbbreviation: d.tmaiSiteAbbreviation.trim(),
    catalogRightsExpiryYears:
      d.catalogRightsExpiryYears === '' ? null : Number(d.catalogRightsExpiryYears),
    ...(d.doctrinalStatement ? { doctrinalStatement: d.doctrinalStatement } : {}),
    landing: {
      ...d.landing,
      featuredEvents: normalizeFeaturedEvents(d.landing.featuredEvents ?? []),
      faculty: normalizeFaculty(d.landing.faculty ?? []),
      facultyIntro: (d.landing.facultyIntro ?? '').trim(),
      pillars: normalizePillars(d.landing.pillars ?? []),
      // Sin datos queda null: no se guarda un objeto vacío.
      contact: normalizeContact(d.landing.contact),
    },
    customDomains: d.customDomains,
    rubrics: d.rubrics
      .filter((r) => r.title.trim() && r.criteria.length > 0)
      .map((r) => ({
        id: r.id,
        title: r.title.trim(),
        criteria: r.criteria.map((c) => ({
          id: c.id,
          label: c.label.trim() || 'Criterio',
          description: c.description.trim() || ' ',
          weight: Number.isFinite(c.weight) ? c.weight : 0,
          ...(c.levels && c.levels.length > 0
            ? {
                levels: c.levels.map((l) => ({
                  id: l.id,
                  name: l.name.trim() || 'Nivel',
                  description: l.description.trim() || ' ',
                })),
              }
            : {}),
        })),
      })),
  } as UpdateTenantSettingsParams;
}
